import React, { useState, useEffect, useRef } from 'react';
import { useDataEngine } from '@dhis2/app-runtime';
import { Card, Modal, ModalTitle, ModalContent, ModalActions, Button, AlertBar } from '@dhis2/ui';
import BellCurveChart from './BellCurveChart';
import { useLeadershipRatings } from '../hooks/useLeadershipRatings';
import { CONFIG } from '../../leadership/api/LeadershipServices';

const RATING_SCALE_NAMESPACE = 'leadership-rating-scale';

const getDefaultColor = (rating) => {
    const colors = {
        1: '#dc3545',
        2: '#ff9800',
        3: '#ffc107',
        4: '#4caf50',
        5: '#28a745'
    };
    return colors[rating] || '#6c757d';
};

const getDefaultScale = () => ({
    1: { label: '1 - Unsatisfactory', color: '#dc3545', description: 'Rarely meets expectations' },
    2: { label: '2 - Developing', color: '#ff9800', description: 'Sometimes meets expectations' },
    3: { label: '3 - Competent', color: '#ffc107', description: 'Meets expectations consistently' },
    4: { label: '4 - Strong Performer', color: '#4caf50', description: 'Frequently exceeds expectations' },
    5: { label: '5 - Role Model', color: '#28a745', description: 'Consistently exceeds expectations' }
});

const getDataStoreKey = (orgId) => orgId ? `scale_${orgId}` : 'scale_global';

const getParentOrgUnitId = async (engine, orgUnitId) => {
    if (!orgUnitId) return null;
    try {
        const response = await engine.query({
            orgUnit: {
                resource: `organisationUnits/${orgUnitId}`,
                params: { fields: 'path,parent[id]' }
            }
        });
        const path = response.orgUnit?.path;
        if (path) {
            const pathParts = path.split('/').filter(Boolean);
            if (pathParts.length >= 5) return pathParts[4];
            return response.orgUnit?.parent?.id || null;
        }
        return null;
    } catch (error) {
        return null;
    }
};

const mapDataValues = (event) => {
    const dataValues = {};
    event.dataValues?.forEach((dv) => {
        dataValues[dv.dataElement] = dv.value;
    });
    return dataValues;
};

const getNumericRating = (dataValues) => {
    const ratingKeys = [
        CONFIG?.de?.ratingScore,
        CONFIG?.de?.managerRatingScore,
        'ratingScore',
        'rating',
        'score'
    ];
    for (const key of ratingKeys) {
        if (!key) continue;
        const value = dataValues[key];
        const num = parseInt(value, 10);
        if (!Number.isNaN(num) && num >= 1 && num <= 5) {
            return num;
        }
    }

    for (const [, value] of Object.entries(dataValues)) {
        const num = parseInt(value, 10);
        if (!Number.isNaN(num) && num >= 1 && num <= 5) {
            return num;
        }
    }
    return 0;
};

const getTextValue = (dataValues, keys) => {
    for (const key of keys) {
        if (!key) continue;
        if (dataValues[key]) return dataValues[key];
    }
    for (const [, value] of Object.entries(dataValues)) {
        const trimmed = String(value || '').trim();
        if (trimmed) return trimmed;
    }
    return '';
};

const getPeriodFromValues = (dataValues, fallbackPeriod) => {
    const periodKeys = [
        CONFIG?.de?.periodType,
        'periodType',
        'reviewPeriod',
        'reviewType',
        'period',
        'assessmentType'
    ];
    for (const key of periodKeys) {
        if (!key || !dataValues[key]) continue;
        const value = String(dataValues[key]).toLowerCase();
        if (value.includes('mid')) return 'Mid-Year';
        if (value.includes('end')) return 'End-Year';
    }
    return fallbackPeriod;
};

const getPeriodMetadata = (event, fallbackPeriod) => {
    const programStage = event.programStage || '';
    if (programStage === CONFIG?.stages?.midYear || /mid/i.test(programStage)) return 'Mid-Year';
    if (programStage === CONFIG?.stages?.endYear || /end/i.test(programStage)) return 'End-Year';
    const dataValues = event.dataValues || [];
    const dataElements = dataValues.map((dv) => dv.dataElement).join(',');
    if (dataElements.includes(CONFIG?.stages?.midYear || '')) return 'Mid-Year';
    if (dataElements.includes(CONFIG?.stages?.endYear || '')) return 'End-Year';
    const dateValue = event.eventDate || event.createdAt || '';
    if (dateValue) {
        const month = new Date(dateValue).getMonth();
        if (month >= 6) return 'Mid-Year';
    }
    return fallbackPeriod;
};

const LeadershipRatingsDashboard = ({
    performanceData,
    accessibleOrgUnitIds,
    filterScope,
    competencies = [],
    ratingScale: propRatingScale = {},
    orgUnitId
}) => {
    const { midYearRatings, endYearRatings } = useLeadershipRatings(performanceData);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedCompetency, setSelectedCompetency] = useState('all');
    const [filteredMidYearData, setFilteredMidYearData] = useState(midYearRatings);
    const [filteredEndYearData, setFilteredEndYearData] = useState(endYearRatings);
    const [ratingScale, setRatingScale] = useState(propRatingScale);
    const [loadingScale, setLoadingScale] = useState(false);
    const [loadingCompetencies, setLoadingCompetencies] = useState(false);
    const [availableCompetencies, setAvailableCompetencies] = useState([]);

    const engine = useDataEngine();
    const propRatingScaleRef = useRef(propRatingScale);

    useEffect(() => {
        propRatingScaleRef.current = propRatingScale;
    }, [propRatingScale]);

    useEffect(() => {
        const fetchRatingScale = async () => {
            const currentPropScale = propRatingScaleRef.current;

            if (currentPropScale && Object.keys(currentPropScale).length > 0) {
                setRatingScale(currentPropScale);
                setLoadingScale(false);
                return;
            }

            if (!orgUnitId) {
                setRatingScale(getDefaultScale());
                setLoadingScale(false);
                return;
            }

            setLoadingScale(true);
            try {
                const parentOrgId = await getParentOrgUnitId(engine, orgUnitId);
                const orgsToCheck = [orgUnitId];
                if (parentOrgId && parentOrgId !== orgUnitId) {
                    orgsToCheck.push(parentOrgId);
                }

                let loadedScale = null;

                for (const orgId of orgsToCheck) {
                    const key = getDataStoreKey(orgId);
                    try {
                        const response = await engine.query({
                            ratingScale: {
                                resource: `dataStore/${RATING_SCALE_NAMESPACE}/${key}`,
                            }
                        });

                        const data = response.ratingScale;
                        if (Array.isArray(data) && data.length > 0) {
                            const mapped = {};
                            data.forEach((item) => {
                                const value = Number(item.value);
                                if (!Number.isNaN(value)) {
                                    mapped[value] = {
                                        label: item.label || `${value}`,
                                        color: item.color || getDefaultColor(value),
                                        description: item.description || ''
                                    };
                                }
                            });

                            if (Object.keys(mapped).length > 0) {
                                loadedScale = mapped;
                                break;
                            }
                        }
                    } catch (error) {
                        console.warn(`No rating scale found for org ${orgId}:`, error);
                    }
                }

                if (!loadedScale) {
                    try {
                        const globalResponse = await engine.query({
                            ratingScale: {
                                resource: `dataStore/${RATING_SCALE_NAMESPACE}/scale_global`,
                            }
                        });

                        const globalData = globalResponse.ratingScale;
                        if (Array.isArray(globalData) && globalData.length > 0) {
                            const mapped = {};
                            globalData.forEach((item) => {
                                const value = Number(item.value);
                                if (!Number.isNaN(value)) {
                                    mapped[value] = {
                                        label: item.label || `${value}`,
                                        color: item.color || getDefaultColor(value),
                                        description: item.description || ''
                                    };
                                }
                            });

                            if (Object.keys(mapped).length > 0) {
                                loadedScale = mapped;
                            }
                        }
                    } catch (error) {
                        console.warn('No global rating scale found:', error);
                    }
                }

                setRatingScale(loadedScale || getDefaultScale());
            } catch (error) {
                console.error('Failed to fetch rating scale:', error);
                setRatingScale(getDefaultScale());
            } finally {
                setLoadingScale(false);
            }
        };

        fetchRatingScale();
    }, [orgUnitId, engine]);

    useEffect(() => {
        const fetchCompetencies = async () => {
            if (!orgUnitId) return;
            setLoadingCompetencies(true);
            try {
                const query = {
                    configEvents: {
                        resource: 'events',
                        params: {
                            program: CONFIG.program,
                            programStage: CONFIG.stages.config,
                            orgUnit: orgUnitId,
                            ouMode: 'ACCESSIBLE',
                            fields: 'event,dataValues[dataElement,value]',
                            pageSize: 100,
                        },
                    },
                };
                const result = await engine.query(query);
                const events = result.configEvents?.events || [];
                const competencies = events
                    .map((event) => {
                        const dataValues = {};
                        event.dataValues?.forEach((dv) => {
                            dataValues[dv.dataElement] = dv.value;
                        });
                        const code = dataValues[CONFIG.de.competencyCode];
                        const definition = dataValues[CONFIG.de.competencyDefinition];
                        const weight = dataValues[CONFIG.de.weightValue];
                        if (!code) return null;
                        return {
                            competencyId: event.event,
                            name: code,
                            definition: definition || code,
                            weight: weight ? Number(weight) : null,
                        };
                    })
                    .filter(Boolean);
                setAvailableCompetencies(competencies);
            } catch (error) {
                console.error('Failed to fetch competencies:', error);
            } finally {
                setLoadingCompetencies(false);
            }
        };

        fetchCompetencies();
    }, [orgUnitId, engine, CONFIG]);

    const handleViewDetails = (period, competencyId = null) => {
        setSelectedPeriod(period);
        setSelectedCompetency(competencyId || 'all');
        setShowDetailsModal(true);
    };
    
    const getDetailedData = () => {
        if (!selectedPeriod) return [];
        
        const rawEvents = selectedPeriod === 'Mid-Year'
            ? (midYearRatings.rawEvents || [])
            : (endYearRatings.rawEvents || []);
        
        return rawEvents
            .map((event) => {
                const dataValues = {};
                event.dataValues?.forEach((dv) => {
                    dataValues[dv.dataElement] = dv.value;
                });
                
                const ratingScore = parseInt(dataValues[CONFIG?.de?.ratingScore], 10);
                const managerRatingScore = parseInt(dataValues[CONFIG?.de?.managerRatingScore], 10);
                const rating = Number.isNaN(ratingScore)
                    ? (Number.isNaN(managerRatingScore) ? 0 : managerRatingScore)
                    : ratingScore;
                
                return {
                    id: event.event,
                    staffName: 'N/A',
                    position: 'N/A',
                    reviewer: event.trackedEntityInstance || 'N/A',
                    rating,
                    ratingLabel: getRatingLabel(rating),
                    comments: dataValues[CONFIG?.de?.ratingComment] || dataValues[CONFIG?.de?.managerRatingComment] || '',
                    reviewDate: event.eventDate?.split('T')[0] || 'N/A',
                    orgUnit: event.orgUnitName || 'N/A'
                };
            })
            .filter((item) => item.rating > 0);
    };
    
    const getRatingLabel = (rating) => {
        if (ratingScale && ratingScale[rating] && ratingScale[rating].label) {
            return ratingScale[rating].label;
        }
        if (rating === 1) return 'Poor';
        if (rating === 2) return 'Below Average';
        if (rating === 3) return 'Average';
        if (rating === 4) return 'Above Average';
        if (rating === 5) return 'Excellent';
        return 'Not Rated';
    };

    const getRatingColor = (rating) => {
        if (ratingScale && ratingScale[rating] && ratingScale[rating].color) {
            return ratingScale[rating].color;
        }
        const colors = {
            1: '#dc3545',
            2: '#ff9800',
            3: '#ffc107',
            4: '#4caf50',
            5: '#28a745'
        };
        return colors[rating] || '#6c757d';
    };
    
    // Debug log to see what data is available
    console.log('LeadershipRatingsDashboard Debug:', {
        performanceDataKeys: performanceData ? Object.keys(performanceData) : 'no data',
        hasLeadership: !!performanceData?.leadership,
        leadershipEventsCount: performanceData?.leadership?.events?.length || 0,
        midYearRatings: midYearRatings,
        endYearRatings: endYearRatings
    });
    
    function getImprovementColor() {
        const improvement = parseFloat(filteredEndYearData.average || endYearRatings.average) - parseFloat(filteredMidYearData.average || midYearRatings.average);
        if (improvement > 0) return '#28a745';
        if (improvement < 0) return '#dc3545';
        return '#ffc107';
    }

    function getImprovementTrend() {
        const mid = parseFloat(filteredMidYearData.average || midYearRatings.average);
        const end = parseFloat(filteredEndYearData.average || endYearRatings.average);
        if (mid === 0) return '0';
        return ((end - mid) / mid * 100).toFixed(1);
    }

    return (
        <>
            {loadingScale ? (
                <Card style={{ padding: '20px', textAlign: 'center' }}>
                    <p>Loading rating scale configuration...</p>
                </Card>
            ) : (
                <>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
                        gap: '24px',
                        marginBottom: '24px'
                    }}>
                        <BellCurveChart
                            title="Mid-Year Review Performance Distribution"
                            data={filteredMidYearData.data || []}
                            period="Mid-Year"
                            ratingScale={ratingScale}
                            competencies={availableCompetencies}
                            onViewDetails={(period) => handleViewDetails(period, selectedCompetency)}
                            showCompetencyFilter={false}
                        />

                        <BellCurveChart
                            title="End-Year Review Performance Distribution"
                            data={filteredEndYearData.data || []}
                            period="End-Year"
                            ratingScale={ratingScale}
                            competencies={availableCompetencies}
                            onViewDetails={(period) => handleViewDetails(period, selectedCompetency)}
                            showCompetencyFilter={false}
                        />
                    </div>

                    {(filteredMidYearData.total > 0 || filteredEndYearData.total > 0) && (
                        <Card style={{ padding: '20px', marginTop: '8px' }}>
                            <h4 style={{ margin: '0 0 16px 0', color: '#2c3e50' }}>Performance Comparison</h4>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '16px'
                            }}>
                                <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                    <div style={{ fontSize: '0.8rem', color: '#666' }}>Mid-Year Avg Rating</div>
                                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#28a745' }}>
                                        {filteredMidYearData.average}
                                    </div>
                                    <div style={{ fontSize: '0.7rem', color: '#999' }}>({filteredMidYearData.total} reviews)</div>
                                </div>
                                <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                    <div style={{ fontSize: '0.8rem', color: '#666' }}>End-Year Avg Rating</div>
                                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#28a745' }}>
                                        {filteredEndYearData.average}
                                    </div>
                                    <div style={{ fontSize: '0.7rem', color: '#999' }}>({filteredEndYearData.total} reviews)</div>
                                </div>
                                {filteredMidYearData.total > 0 && filteredEndYearData.total > 0 && (
                                    <>
                                        <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                            <div style={{ fontSize: '0.8rem', color: '#666' }}>Improvement Trend</div>
                                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: getImprovementColor() }}>
                                                {getImprovementTrend()}%
                                            </div>
                                            <div style={{ fontSize: '0.7rem', color: '#999' }}>Mid to End-Year</div>
                                        </div>
                                        <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                            <div style={{ fontSize: '0.8rem', color: '#666' }}>Top Performers (5)</div>
                                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#28a745' }}>
                                                {filteredEndYearData.distribution?.[5] || 0}
                                            </div>
                                            <div style={{ fontSize: '0.7rem', color: '#999' }}>End-Year</div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </Card>
                    )}
                </>
            )}
            {showDetailsModal && (
                <Modal large onClose={() => setShowDetailsModal(false)}>
                    <ModalTitle>
                        {selectedPeriod} Review Details
                    </ModalTitle>
                    <ModalContent>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontSize: '0.85rem'
                            }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                        <th style={{ padding: '12px', textAlign: 'left' }}>Staff Name</th>
                                        <th style={{ padding: '12px', textAlign: 'left' }}>Position</th>
                                        <th style={{ padding: '12px', textAlign: 'left' }}>Reviewer</th>
                                        <th style={{ padding: '12px', textAlign: 'center' }}>Rating</th>
                                        <th style={{ padding: '12px', textAlign: 'left' }}>Review Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getDetailedData().map(item => (
                                        <tr key={item.id} style={{ borderBottom: '1px solid #e9ecef' }}>
                                            <td style={{ padding: '10px' }}>{item.staffName}</td>
                                            <td style={{ padding: '10px' }}>{item.position}</td>
                                            <td style={{ padding: '10px' }}>{item.reviewer}</td>
                                            <td style={{ padding: '10px', textAlign: 'center' }}>
                                                <span style={{
                                                    display: 'inline-block',
                                                    padding: '4px 8px',
                                                    borderRadius: '12px',
                                                    backgroundColor: getRatingColor(item.rating),
                                                    color: 'white',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {item.rating} - {item.ratingLabel}
                                                </span>
                                            </td>
                                            <td style={{ padding: '10px' }}>{item.reviewDate}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </ModalContent>
                    <ModalActions>
                        <Button onClick={() => setShowDetailsModal(false)}>Close</Button>
                    </ModalActions>
                </Modal>
            )}
        </>
    );
};

export default LeadershipRatingsDashboard;